'use client';

import classnames from 'classnames';
import { type FC, Fragment, useEffect, useState } from 'react';

import Button from 'molecules/Button';
import Image from 'molecules/Image';

import styles from 'components/Heros/QuoteHero/quote-full-width-block.module.scss';

import type { QuoteHeroProps } from 'components/Heros/QuoteHero/config';

const learningCenterPage = process.env.NEXT_PUBLIC_LEARNING_CENTER_SITE_DOMAIN;
const savedQuotesPage = process.env.NEXT_PUBLIC_SAVED_QUOTES_SITE;
const { NODE_ENV } = process.env;
const onlineQuote = process.env.NEXT_PUBLIC_GET_A_QUOTE_ONLINE || '';
const agentQuote = '/agentquote';

const QuoteHero: FC<QuoteHeroProps> = ({ heading, borderBottomImage, backgroundImage, subhead }) => {
  const bgImage = backgroundImage?.url ? { backgroundImage: `url(${backgroundImage.url})` } : {};
  const registerPage = `${learningCenterPage}/register`;
  const [cookieValue, setCookieValue] = useState<{ name: string; value: string } | null>(null);
  const [sessionId, setSessionId] = useState<{ name: string; value: string } | null>(null);
  const [formattedAgentQuote, setFormattedAgentQuote] = useState<string>(agentQuote);

  const setCTACookies = (CtaUrl: string): string | null => {
    if (cookieValue && sessionId && !!CtaUrl) {
      const sanitizedName = cookieValue.name.replace(/^_/, '');

      return `${CtaUrl}?${sanitizedName}=${cookieValue.value}&sid=${sessionId.value}`;
    } else {
      return null;
    }
  };

  useEffect(() => {
    const fetchCookie = async (cookieName: string, isSessCookie?: boolean) => {
      try {
        const response = await fetch(`/api/cookies/?name=${cookieName}`);
        const data = await response.json();

        if (data.cookie === 'Not found') {
          return;
        }

        if (isSessCookie) {
          return setSessionId({ name: cookieName, value: data.cookie });
        } else {
          return setCookieValue({ name: cookieName, value: data.cookie });
        }
      } catch (error) {
        console.error(`Error fetching cookie "${cookieName}":`, error);
      }
    };
    const isProduction = NODE_ENV !== 'development';
    const sessID = isProduction ? '_ga_B3QQD8LXK7' : '_ga_1ETK66H7KH';
    fetchCookie('_ga');
    fetchCookie(sessID, true);
  }, []);

  useEffect(() => {
    const agentQuoteUrl = setCTACookies(agentQuote) ?? agentQuote;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormattedAgentQuote(agentQuoteUrl);
  }, [cookieValue, sessionId]);

  return (
    <Fragment>
      <section
        className={classnames(styles.wrapper, {
          [styles.image]: bgImage,
        })}
        style={bgImage}
      >
        <div className={styles.textContainer}>
          {heading && <h1>{heading}</h1>}
          {subhead && <h4 className={styles.body}>{subhead}</h4>}
          <Button url={onlineQuote} color="grayBlue" className={styles.cta}>
            GET A QUOTE ONLINE
          </Button>
          {/* eslint-disable-next-line no-inline-styles/no-inline-styles */}
          <span style={{ padding: '2.4rem 2rem', fontSize: '1.6rem' }}> OR </span>
          <Button url={formattedAgentQuote} color="grayBlue" className={styles.cta}>
            QUOTE WITH AGENT
          </Button>
          <Button
            url={savedQuotesPage}
            direction="right"
            color="whiteArrow"
            className={styles.savedQuotesButton}
            iconId="arrow-right"
          >
            View Saved Quotes
          </Button>

          <div className={styles.accountButtons}>
            <p>
              Already have an account?{' '}
              {
                <Button color="whiteArrow" url={learningCenterPage} className={styles.whiteButton}>
                  Sign in
                </Button>
              }
            </p>
            <p>
              New User?{' '}
              {
                <Button color="whiteArrow" url={registerPage} className={styles.whiteButton}>
                  Create Account
                </Button>
              }
            </p>
          </div>
        </div>
      </section>
      {borderBottomImage?.url && (
        <div className={styles.border}>
          <Image url={borderBottomImage?.url} fill alt="Border Bottom Background Image" />
        </div>
      )}
    </Fragment>
  );
};

export default QuoteHero;
